import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import { useAdbiClickCapture, useAdbiIdentify } from 'src/hooks/useAdbi'
import { useMyInfo } from 'src/services/apollo/auth'
import { useMyMembership } from 'src/services/apollo/membership'
import { useMyQuota } from 'src/services/apollo/quota'
import { useContentSafetyPreferences } from 'src/services/orpc/contentSafetyPreference'
import { localeAtom } from 'src/stores/locale'
import dayjs from 'src/utils/dayjs'
import {
  reportGtagEnhancedUserData,
  trackerSetUserId,
  trackerSetUserProperties,
} from 'src/utils/tracker'
import { UserSubscriptionStatus } from 'src/utils/user'

/**
 * Renders nothing, but tracks user's basic info and report them to server
 * These properties should be collected from server side
 * Remove this tracker once server side tracking data collector is implemented
 */
function UserInfoTracker() {
  const { data: userInfo } = useMyInfo()

  useAdbiClickCapture()
  useAdbiIdentify(userInfo?.id)
  const {
    data: { membership, subscription } = {},
    loading: isLoadingMembership,
  } = useMyMembership()
  const { data: userQuota } = useMyQuota()

  useEffect(() => {
    if (isLoadingMembership) return
    if (
      membership?.tier != null &&
      subscription?.interval != null &&
      subscription.status === UserSubscriptionStatus.active
    ) {
      const planStr = `${subscription.interval.toLowerCase()}-plan-${
        membership.tier
      }`
      const daysLeft = subscription.endAt
        ? dayjs.duration(dayjs(subscription.endAt).diff(dayjs())).days()
        : 'none' // Invalid subscription.endAt
      trackerSetUserProperties({
        // `lastPlan` unlike `plan`
        // it won't be reset to 'free' on membership ending
        lastPlan: planStr,
        plan: planStr,
        membershipLeft: daysLeft.toString(),
      })
    } else {
      trackerSetUserProperties({
        plan: 'free',
        membershipLeft: 'none',
      })
    }
  }, [
    membership?.tier,
    subscription?.endAt,
    subscription?.interval,
    isLoadingMembership,
  ])

  useEffect(() => {
    if (userInfo) {
      trackerSetUserId(userInfo.id, { isAdmin: userInfo.isAdmin })
      trackerSetUserProperties({
        followerCount: userInfo.followerCount,
        followingCount: userInfo.followingCount,
      })
    }
  }, [userInfo?.followerCount, userInfo?.followingCount, userInfo?.id])

  useEffect(() => {
    if (userInfo?.email) {
      reportGtagEnhancedUserData(userInfo.email)
    }
  }, [userInfo?.email])

  useEffect(() => {
    if (userInfo && userInfo.isAdmin) {
      trackerSetUserProperties({
        isAdmin: userInfo.isAdmin,
      })
    }
  }, [userInfo?.isAdmin])

  useEffect(() => {
    if (userQuota != null) {
      trackerSetUserProperties({
        creditsLeft: userQuota,
      })
    }
  }, [userQuota])

  useEffect(() => {
    trackerSetUserProperties({
      user_agent: navigator.userAgent,
    })
  }, [])

  useEffect(() => {
    trackerSetUserProperties(null, {
      initial_platform: 'Web',
      initial_url: window.location.href.split('?')[0],
    })
  }, [])

  const locale = useAtomValue(localeAtom)
  useEffect(() => {
    if (locale) {
      trackerSetUserProperties({
        language: locale,
      })
    }
  }, [locale])

  return null
}

/**
 * Renders nothing, but tracks user's preferences and report them to server
 * These properties should be collected from server side
 * Remove this tracker once server side tracking data collector is implemented
 */
function UserPreferencesTracker() {
  const { data, isLoading } = useContentSafetyPreferences()

  useEffect(() => {
    if (isLoading || !data) return
    const showNsfw = data.showNsfw?.value
    const blurNsfw = data.blurNsfw?.value
    const ageVerified = data.ageVerified?.value
    trackerSetUserProperties({
      showNsfw: showNsfw != null ? showNsfw === 'show' : undefined,
      blurNsfw: blurNsfw != null ? blurNsfw === 'blur' : undefined,
      ageVerificationStatus:
        ageVerified != null ? ageVerified === 'adult' : undefined,
    })
  }, [
    data?.showNsfw?.value,
    data?.blurNsfw?.value,
    data?.ageVerified?.value,
    isLoading,
    data,
  ])

  return null
}

export function UserPropertiesTracker() {
  return (
    <>
      <UserInfoTracker />
      <UserPreferencesTracker />
    </>
  )
}
